import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import React, { useGlobal } from 'reactn'
import { useNavigate, useParams } from 'react-router-dom'
import MedSetting from 'ui/pages/med/Setting'
import nullToUndefined from 'ui/utils/nullToUndefined'
import {
  newDepartmentSettings,
  removeVideoAppSettings,
  sideBarRoute,
  videoAppUniqueError,
} from './utils'
import {
  getDepartmentList,
  putDepartmentDisplayOrder,
  postStripeStandardAccounts,
  deleteStripeStandardAccounts,
  getBankTransferSettings,
  postBankTransferSettings,
  putBankTransferSettings,
  getDepartmentSettings,
  postVideoAppConnections,
  deleteVideoAppConnections,
  putDepartmentSettings,
  postDepartmentSettings,
  deleteDepartmentSettings,
  getReservations,
  putContactEmails,
  getContactEmails,
  searchPharmacies,
  getPharmacies,
  putPharmacies,
  putPrescription,
  getPrescription,
  getGoogleMapsApiKey,
  updateEligibilityVerificationUrl,
} from './fetch'
import type {
  BankTransfer,
  ContactEmails,
  DepartmentSettingsProps,
  PrescriptionSetting,
} from 'types/med'
import type {
  CallMethodNames,
  CallMethodVideoAppNames,
  DepartmentListItemProps,
  PharmacyProps,
} from 'types/common'
import GeneralSetting from 'ui/pages/med/Setting/General'
import DepartmentEdit from 'ui/pages/med/Setting/DepartmentEdit'
import type { DepartmentEditRefProps } from 'ui/pages/med/Setting/DepartmentEdit'
import { routePaths } from 'app/routes'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'
import DepartmentRead from 'ui/pages/med/Setting/DepartmentRead'
import DepartmentReadControlBar from 'ui/pages/med/Setting/DepartmentReadControlBar'
import ConfirmDiscardModal from 'ui/pages/med/Setting/ConfirmDiscardModal'
import { connectOAuth, OAuthResponse } from './OAuth'
import { originUrl } from 'config/url'
import { Api401Error, ApiErrorWithMessage, NotFoundError } from '../client'

const Setting: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { department_id } = useParams<'department_id'>() as {
    department_id: string
  }

  const latestDepartmentIdRef = useRef(Number(department_id))
  latestDepartmentIdRef.current = Number(department_id)

  const [user, setUser] = useGlobal('user')
  const [update] = useGlobal('updateInformation')

  const [appClientId] = useGlobal('appClientId')
  const [googleMapsApiKey, setGoogleMapsApiKey] = useGlobal('googleMapsApiKey')

  // NOTE: /setting/以下ページ遷移表示を高速化するキャッシュのためのオブジェクト
  const [departmentListItems, setDepartmentListItems] = useGlobal(
    'departmentListItems'
  )
  // NOTE: /setting/department/以下ページ遷移表示を高速化するキャッシュのためのオブジェクト
  const [departmentSettingsObj, setDepartmentSettingsObj] = useGlobal(
    'departmentSettingsObj'
  )

  const [departmentSettings, setDepartmentSettings] = useState<
    DepartmentSettingsProps | undefined
  >()

  const [bankTransferSettings, setBankTransferSettings] = useState<
    BankTransfer | undefined
  >()

  const [editable, setEditable] = useState<boolean>(false)
  const [creating, setCreating] = useState<boolean>(false)
  const [connectVideoAppResult, setConnectVideoAppResult] = useState<
    CallMethodVideoAppNames | { error: string }
  >()

  const [contactEmails, setContactEmails] = useState<ContactEmails>([])
  const [pharmacies, setPharmacies] = useState<PharmacyProps[]>()
  const [prescriptionSetting, setPrescriptionSetting] =
    useState<PrescriptionSetting>({
      prescription_medicine: 'direct',
      is_todokusuri: null,
    })

  // NOTE: 削除時モーダル
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false)

  // Stripe設定がサーバー側とコンフリクトした時のモーダル
  const [showSettlementSettingConflict, setShowSettlementSettingConflict] =
    useState<boolean>(false)

  const [reloadTrigger, setReloadTrigger] = useState(0) // NOTE: useEffectを走らせるための更新カウンタ

  /*
  DepartmentList
   */

  const getDepartmentListHandler = () =>
    getDepartmentList().then(setDepartmentListItems).catch(console.log)

  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false)
  const [discardAction, setDiscardAction] = useState<() => void | undefined>()
  const editRef = useRef<DepartmentEditRefProps>()

  const confirmDiscard = (onDiscard: () => void) => {
    if (editable && editRef.current?.changed?.()) {
      setShowConfirmDiscard(true)
      setDiscardAction(() => onDiscard)
    } else {
      onDiscard()
    }
  }

  const changeDepartmentOrder = (
    id: DepartmentListItemProps['id'],
    order: number
  ) => {
    putDepartmentDisplayOrder(id, order)
      .then(getDepartmentListHandler)
      .catch(console.log)
  }

  const selectDepartmentHandler = (
    departmentId: DepartmentListItemProps['id']
  ) => {
    if (departmentId === departmentSettings?.id) return // NOTE: 同じ受付科目なら遷移しない

    confirmDiscard(() => {
      // NOTE: 高速化処理 画面が切り替わったら先にキャッシュで表示、遷移後裏側で再度読み込んで密かに更新する
      if (departmentSettingsObj && departmentSettingsObj[departmentId]) {
        setDepartmentSettings(departmentSettingsObj[departmentId])
      }
      setTimeout(() => {
        // NOTE: アニメーション待ち
        navigate(
          `${routePaths.medical}${routePaths.setting}/department/${departmentId}`
        )
      }, 300)
    })
  }

  /*
  DepartmentSetting
   */

  const createDepartment = () => {
    confirmDiscard(() => {
      postDepartmentSettings({
        ...newDepartmentSettings,
        display_order: departmentListItems && departmentListItems.length + 1,
      })
        .then(() => {
          setTimeout(() => {
            getDepartmentList().then((data) => {
              setDepartmentListItems(data).then(() => {
                if (!data?.length) return
                const newId = data[data.length - 1].id
                navigate(
                  `${routePaths.medical}${routePaths.setting}/department/${newId}`
                )
                setCreating(true)
              })
            })
          }, 300)
        })
        .catch(console.log)
        .finally(() => setCreating(false))
    })
  }

  const updateDepartmentHandler: (
    data: DepartmentSettingsProps,
    reload?: boolean,
    keepEditable?: boolean
  ) => Promise<void> = (data, reload = true, keepEditable = false) => {
    if (!keepEditable) {
      setEditable(false)
    }
    const currentListItem: DepartmentListItemProps = departmentListItems?.find(
      (item) => item.id === Number(department_id)
    )

    const newData = {
      ...data,
      display_order: currentListItem?.display_order,
      // NOTE: 接続、解除のアクションと矛盾が起きないようにここではビデオ通話アプリの設定は送信せず、サーバ側のみで保存する
      call_methods: removeVideoAppSettings(data.call_methods),
    }

    return putDepartmentSettings(Number(department_id), newData)
      .then(() => {
        reload && getDepartmentSettingsHandler()
      })
      .catch((e) => {
        if (e instanceof ApiErrorWithMessage) {
          if (
            e.has({
              type: 'connection_error',
              key: 'settlement_settings',
              value: 'conflict',
            })
          ) {
            setShowSettlementSettingConflict(true)
          }
        }
      })
      .finally(() => {
        // NOTE: 新規で保存した時に編集モードに戻ってしまうため
        setCreating(false)
      })
  }

  const deleteDepartmentSettingsHandler = () => {
    // NOTE: 新規作成直後の時の対応
    setEditable(false)
    setCreating(false)

    deleteDepartmentSettings(Number(department_id))
      .then(() => {
        navigate(`${routePaths.medical}${routePaths.setting}`)
        setReloadTrigger((prev) => prev + 1)
      })
      .catch((e) => {
        if (e instanceof NotFoundError) {
          return
        }
        if (e instanceof Api401Error) {
          return
        }
        console.log(e)
        navigate(routePaths.error)
      })
  }

  /*
  Bank
   */
  const saveBankTransferHandler = (data: BankTransfer) =>
    !bankTransferSettings?.bank_name
      ? postBankTransferSettings(data).then(() => setBankTransferSettings(data))
      : putBankTransferSettings(data).then(() => setBankTransferSettings(data))

  /*
  VideoApp connect
   */
  const connectVideoAppHandler: (
    app: CallMethodVideoAppNames
  ) => Promise<void> = (app) => {
    let opened = false
    switch (app) {
      case 'video_teams':
        opened = connectOAuth('teams', appClientId['teams'])
        break
      case 'video_zoom':
        opened = connectOAuth('zoom', appClientId['zoom'])
        break
    }
    return new Promise((resolve, reject) => {
      if (!opened) {
        setConnectVideoAppResult({
          error: t('errors.ポップアップがブロックされました'),
        })
        reject()
      } else {
        // TODO: connectVideoAppResultを介さずOAuthウィンドウからきたcodeを
        //  poetMessageで受け取りサーバーに投げた結果を受け取るのを待ってからresolveまたはrejectにした方が良い
        resolve()
      }
    })
  }

  const disconnectVideoAppHandler: (
    app: CallMethodVideoAppNames
  ) => Promise<void> = (app) => {
    return deleteVideoAppConnections({
      app,
      department_id: Number(department_id),
    })
      .then(() => {
        // NOTE: 接続解除した瞬間は必ずreservation_messageが空なのでcall_methodsは常に電話のみONのデフォルトにする
        return (
          departmentSettings &&
          updateDepartmentHandler(
            {
              ...nullToUndefined<DepartmentSettingsProps>(departmentSettings),
              call_methods: newDepartmentSettings.call_methods,
            },
            false,
            true
          )
        )
      })
      .catch((e) => {
        // NOTE: すでに削除済みの場合はOKにする
        if (e instanceof NotFoundError) {
          return Promise.resolve()
        } else {
          return Promise.reject()
        }
      })
  }

  /*
  Stripe
   */
  const deleteStripeHandler = () =>
    deleteStripeStandardAccounts().then(() => {
      setUser({ ...user, is_connected_stripe: false })
    })

  // NOTE: stripe,zoom の OAuthから来るcodeをmessageEventで受信
  const receiveMessage = (event: MessageEvent) => {
    if (!user) return
    if (event.origin !== originUrl) {
      return
    }
    // NOTE: StripeのOAuth成功
    if (event.data?.connect_stripe) {
      const connectStripe: OAuthResponse = event.data.connect_stripe
      if (connectStripe?.error) {
        const error = connectStripe?.error
        console.log(error) // TODO: 全てのパターンがわかったら削除する
        setConnectVideoAppResult({
          error: t(
            'errors.エラーが発生しました。画面を再読み込みしてください。'
          ),
        })
        return
      }
      postStripeStandardAccounts({
        code: connectStripe?.code,
      }).then(() => {
        setUser({ ...user, is_connected_stripe: true })
      })
      return
    }

    // NOTE: ZoomのOAuth成功
    if (event.data?.connect_zoom) {
      const connectZoom: OAuthResponse = event.data.connect_zoom
      if (connectZoom?.error) {
        const error = connectZoom?.error
        console.log(error) // TODO: 全てのパターンがわかったら削除する
        setConnectVideoAppResult({
          error: t(
            'errors.エラーが発生しました。画面を再読み込みしてください。'
          ),
        })
        return
      }
      postVideoAppConnections({
        app: 'video_zoom',
        department_id: Number(department_id),
        data: {
          code: connectZoom?.code,
        },
      })
        .then(() => setConnectVideoAppResult('video_zoom'))
        .catch((e) => {
          if (e instanceof ApiErrorWithMessage) {
            if (
              e.has({
                key: 'other_video_call_connection',
                value: 'exists',
              })
            ) {
              setConnectVideoAppResult({
                error: t(
                  'errors.すでにビデオ通話アプリに接続されています。画面を再読み込みしてください。'
                ),
              })
              return
            }
            const error = videoAppUniqueError({
              med: user,
              list: departmentListItems || [],
              error: e,
            })
            if (error?.zoom_account?.message) {
              setConnectVideoAppResult({ error: error.zoom_account.message })
              return
            }
            // NOTE：その他想定外のエラー
            setConnectVideoAppResult({
              error: t(
                'errors.エラーが発生しました。画面を再読み込みしてください。'
              ),
            })
          }
        })
      return
    }

    // NOTE: TeamsのOAuth取得成功
    if (event.data?.connect_teams) {
      const connectTeams: OAuthResponse = event.data.connect_teams
      if (connectTeams?.error) {
        const error = connectTeams?.error
        console.log(error) // TODO: 全てのパターンがわかったら削除する
        if (error.type === 'access_denied') {
          setConnectVideoAppResult({
            error: t(
              'errors.エラーが発生しました。画面を再読み込みしてください。'
            ),
          })
        }
        if (error.type === 'invalid_client') {
          setConnectVideoAppResult({
            error: t('errors.このアカウントではミーティングが作成できません。'),
          })
          return
        }
        setConnectVideoAppResult({
          error: t(
            'errors.エラーが発生しました。画面を再読み込みしてください。'
          ),
        })
        return
      }
      postVideoAppConnections({
        app: 'video_teams',
        department_id: Number(department_id),
        data: {
          code: connectTeams?.code,
        },
      })
        .then(() => setConnectVideoAppResult('video_teams'))
        .catch((e) => {
          if (e instanceof ApiErrorWithMessage) {
            if (
              e.has({
                key: 'other_video_call_connection',
                value: 'exists',
              })
            ) {
              setConnectVideoAppResult({
                error: t(
                  'errors.すでにビデオ通話アプリに接続されています。画面を再読み込みしてください。'
                ),
              })
              return
            }
            if (
              e.has({
                key: 'authentication',
              })
            ) {
              setConnectVideoAppResult({
                error: t(
                  'errors.このアカウントではミーティングが作成できません。'
                ),
              })
              return
            }
            const error = videoAppUniqueError({
              med: user,
              list: departmentListItems || [],
              error: e,
            })
            if (error?.teams_account?.message) {
              setConnectVideoAppResult({ error: error.teams_account.message })
              return
            }
            setConnectVideoAppResult({
              error: t(
                'errors.エラーが発生しました。画面を再読み込みしてください。'
              ),
            })
            return
          }
          // NOTE：その他想定外のエラー
          setConnectVideoAppResult({
            error: t(
              'errors.エラーが発生しました。画面を再読み込みしてください。'
            ),
          })
        })
    }
  }

  useEffect(() => {
    if (creating) {
      setEditable(true)
    } else {
      setEditable(false)
    }
    getUnFinishedCallReservations()

    setReloadTrigger((prev) => prev + 1)

    window.addEventListener('message', receiveMessage, false)
    return () => {
      window.removeEventListener('message', receiveMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentSettingsObj])

  const getDepartmentSettingsHandler = () => {
    getDepartmentSettings(Number(department_id))
      .then((data) => {
        setDepartmentSettingsObj({
          ...departmentSettingsObj,
          [Number(department_id)]: data,
        }).then(() => {
          // NOTE: 現在表示中のdepartmentIdの結果以外は更新させない
          if (latestDepartmentIdRef.current !== Number(department_id)) return
          setDepartmentSettings(data)
        })
      })
      .catch(() => {
        // NOTE: 現在表示中のdepartmentIdの結果以外は破棄
        if (latestDepartmentIdRef.current !== Number(department_id)) return
      })
  }

  useEffect(() => {
    // NOTE： 編集モードを切り替えた時に外部エラーをリセット
    setConnectVideoAppResult(undefined)
  }, [editable])

  useEffect(() => {
    getIsDeletableSetting()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentSettings?.id])

  useEffect(() => {
    // NOTE: department_idが変わった時の初期化処理
    if (department_id) {
      setEditable(false)
      getDepartmentSettingsHandler()
    } else {
      setDepartmentSettings(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department_id])

  useEffect(() => {
    getPharmacies()
      .then((data) => data && setPharmacies(data))
      .catch(console.log)
    getContactEmails().then(setContactEmails).catch(console.log)
    getGoogleMapsApiKey()
      .then((data) => {
        data && setGoogleMapsApiKey(data.api_key)
      })
      .catch(console.log)
    getPrescription()
      .then(
        (data) => data && setPrescriptionSetting(data) // NOTE: あとで追加になった値のためnullの時がある
      )
      .catch(console.log)
    getBankTransferSettings().then(setBankTransferSettings).catch(console.log)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [hasUnFinishedCallReservation, setHasUnFinishedCallReservation] =
    useState(false)

  const getUnFinishedCallReservations = () => {
    if (!departmentSettings) return

    // NOTE: ビデオ通話アプリの時だけ未完了の予約をチェック
    const activeVideoAppName: CallMethodNames | undefined =
      departmentSettings.call_methods?.find(
        (m) => m.name.startsWith('video_') && m.is_active
      )?.name

    activeVideoAppName &&
      getReservations({
        not_finished_call: activeVideoAppName,
        department_id: departmentSettings.id,
        limit: 1,
        offset: 0,
      })
        .then((reservations) => {
          // NOTE: 表示中のdepartmentIdの結果以外は破棄
          if (Number(latestDepartmentIdRef.current) !== departmentSettings.id) {
            return
          }
          setHasUnFinishedCallReservation(reservations.length > 0)
        })
        .catch(() => {
          // NOTE: 表示中のdepartmentIdの結果以外は破棄
          if (Number(latestDepartmentIdRef.current) !== departmentSettings.id) {
            return
          }
        })
  }

  const [unDeletableSetting, setUnDeletableSetting] = useState(true)

  const getIsDeletableSetting = () => {
    if (!departmentSettings) return

    getReservations({
      is_completed: false,
      department_id: departmentSettings.id,
      limit: 1,
      offset: 0,
    })
      .then((reservations) => {
        setUnDeletableSetting(reservations.length > 0)
      })
      .catch(console.log)
  }

  useEffect(() => {
    if (!user) return
    getDepartmentListHandler()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger])

  const removeDepartmentModal = (
    <Modal open={showRemoveModal} handleClose={() => setShowRemoveModal(false)}>
      <ConfirmContent
        onSubmit={() => {
          deleteDepartmentSettingsHandler()
          setShowRemoveModal(false)
        }}
        submitLabel={t('削除する')}
        onCancel={() => setShowRemoveModal(false)}
        title={
          <>
            {t('受付科目を削除しますか？')}
            <br />
            {t(
              '予約の確認や請求ができなくなります。この操作は取り消せません。'
            )}
          </>
        }
      />
    </Modal>
  )

  // NOTE: Stripe設定に矛盾が起きてモーダルを閉じる時の復帰アクション
  const reloadSettlementSetting = () => {
    if (!user) return
    getDepartmentSettingsHandler()
    setUser({ ...user, is_connected_stripe: false })
  }

  const settlementSettingConflictModal = (
    <Modal
      open={showSettlementSettingConflict}
      handleClose={() => {
        reloadSettlementSetting()
        setShowSettlementSettingConflict(false)
      }}
    >
      <ConfirmContent
        onSubmit={() => {
          reloadSettlementSetting()
          setShowSettlementSettingConflict(false)
        }}
        title={t('errors.設定保存時にエラーが発生しました')}
      >
        {t('errors.編集ボタンを押して設定し直してください')}
      </ConfirmContent>
    </Modal>
  )

  if (!user) return null

  return (
    <MedSetting
      med={user}
      update={update}
      selectRoute={{
        route: 'setting',
        onSelect: (to) =>
          confirmDiscard(() =>
            sideBarRoute(navigate)(
              Number(department_id) ? undefined : 'setting'
            )(to)
          ),
      }}
      settingSubmenu={{
        generalMenu: {
          current: !Number(department_id),
          onClick: Number(department_id)
            ? () =>
                confirmDiscard(() =>
                  navigate(`${routePaths.medical}${routePaths.setting}`)
                )
            : undefined,
        },
        department: departmentListItems
          ? {
              list: departmentListItems,
              onSelect: selectDepartmentHandler,
              currentId: Number(department_id),
              onChange: changeDepartmentOrder,
              onCreate: createDepartment,
            }
          : undefined,
      }}
      controlBar={
        departmentSettings &&
        !editable && (
          <DepartmentReadControlBar
            onClickEdit={() => setEditable(true)}
            onClickDelete={() => setShowRemoveModal(true)}
            unDeletable={unDeletableSetting}
          />
        )
      }
    >
      {departmentSettings && appClientId ? ( // NOTE: path:department_idがある時に受付科目詳細
        editable ? (
          <DepartmentEdit
            med={user}
            bank={bankTransferSettings}
            defaultValues={{
              // NOTE: 追加になったデフォルト値をセット
              ...nullToUndefined<DepartmentSettingsProps>(departmentSettings),
              reservation_deadline_timing:
                departmentSettings?.reservation_deadline_timing ||
                newDepartmentSettings.reservation_deadline_timing,
              reservation_frame_start_time:
                departmentSettings?.reservation_frame_start_time ||
                newDepartmentSettings.reservation_frame_start_time,
              reservation_frame_end_time:
                departmentSettings?.reservation_frame_end_time ||
                newDepartmentSettings.reservation_frame_end_time,
            }}
            onSubmit={updateDepartmentHandler}
            unDeletable={unDeletableSetting}
            onClickDelete={() => setShowRemoveModal(true)}
            onClickCancel={() =>
              confirmDiscard(() => {
                setEditable(false)
                getDepartmentSettingsHandler()
              })
            }
            onClickConnectVideoApp={connectVideoAppHandler}
            onClickDisconnectVideoApp={disconnectVideoAppHandler}
            connectVideoAppResult={connectVideoAppResult}
            hasUnFinishedCallReservation={hasUnFinishedCallReservation}
            ref={(instance) => {
              if (instance) editRef.current = instance
            }}
          >
            {settlementSettingConflictModal}
            <ConfirmDiscardModal
              open={showConfirmDiscard}
              onCancel={() => {
                setDiscardAction(undefined)
                setShowConfirmDiscard(false)
              }}
              onDiscard={() => {
                discardAction?.()
                setEditable(false)
                setCreating(false)
                setDiscardAction(undefined)
                setShowConfirmDiscard(false)
              }}
            />
          </DepartmentEdit>
        ) : (
          <DepartmentRead med={user} settings={departmentSettings} />
        )
      ) : (
        !department_id &&
        appClientId && (
          <GeneralSetting
            med={user}
            stripe={{
              connected: !!user.is_connected_stripe,
              onConnect: () => {
                // TODO: Promiseを返すようにする
                connectOAuth('stripe', appClientId.stripe)
              },
              onDelete: deleteStripeHandler,
            }}
            bank={{
              setting: bankTransferSettings,
              onSubmit: saveBankTransferHandler,
            }}
            email={{
              list: contactEmails,
              onUpdate: (data) =>
                putContactEmails(data).then(() =>
                  getContactEmails().then(setContactEmails)
                ),
            }}
            prescription={{
              setting: prescriptionSetting,
              onSubmit: (data) =>
                putPrescription(data).then(() => setPrescriptionSetting(data)),
            }}
            pharmacy={{
              pharmacies,
              apiKey: googleMapsApiKey,
              onSearch: searchPharmacies,
              onUpdate: (array) =>
                putPharmacies(array).then(() =>
                  getPharmacies().then((data) => data && setPharmacies(data))
                ),
            }}
            eligibilityVerification={{
              url: user?.online_eligibility_verification_url,
              onUpdate: (url) =>
                updateEligibilityVerificationUrl({
                  online_eligibility_verification_url: url,
                }).then(() => {
                  // NOTE: グローバルステートに保存
                  setUser({ ...user, online_eligibility_verification_url: url })
                }),
            }}
          />
        )
      )}
      {settlementSettingConflictModal}
      {removeDepartmentModal}
    </MedSetting>
  )
}

export default Setting
